import { useCallback, useEffect, useMemo, useState } from 'react';
import { LOCALES, LOCALE_LABEL } from '@icetus/contracts';
import type { Locale, OnboardingView } from '@icetus/contracts';
import { Button } from '@icetus/ui';
import { ApiError, fetchOnboarding, publishOnboarding, updateOnboarding } from '../api';
import { useConfirm } from './confirm';

/**
 * 「让你的站点上线」卡片。
 *
 * 它同时是两样东西：客户注册后第一次进来看到的引导，以及一直留在
 * 「我的站点」页顶部的完成度清单。**不弹窗、不强制走完** ——
 * 客户可以直接去编辑内容，回头再看这张卡还差什么。
 *
 * 为什么不做成四步翻页向导：翻页要求客户在"下一步"和"我还没想好"之间选一个，
 * 而这里三件事（名字、地址、语言）本来就在同一个表单里有默认值。
 * 真正需要单独确认的只有"发布"，它不能和别的动作捆在一起 ——
 * 发布意味着这个站开始对外可见了。
 */

interface FormState {
  siteName: string;
  slug: string;
  defaultLocale: Locale;
  locales: Locale[];
}

function fromView(view: OnboardingView): FormState {
  return {
    siteName: view.site.siteName,
    slug: view.site.slug,
    defaultLocale: view.localeSettings.defaultLocale,
    locales: view.localeSettings.locales,
  };
}

export function OnboardingCard({
  tenantId,
  emailVerified,
  onNavigate,
}: {
  tenantId: string;
  emailVerified: boolean;
  /** 清单里那几项要能把客户送到对应的页面，而不是让他自己找 */
  onNavigate: (key: 'content' | 'catalog' | 'settings' | 'domains') => void;
}) {
  const [view, setView] = useState<OnboardingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState<'save' | 'publish' | null>(null);
  /**
   * 卡片正在说哪个站。
   *
   * `null` = "还没选，让服务端给主站"。不初始化成主站 id 是为了少一次请求：
   * 首次加载本来就要拿一次视图，从中读到的 `site.siteId` 就是默认值。
   * 客户切到第二个站之后这里才变成具体 id，从此读、改、发布都带着它 ——
   * 三者必须同一个值，否则会出现"看的是 A 站、发布的是 B 站"，而两边都不报错。
   */
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    let cancelled = false;
    fetchOnboarding(tenantId, selectedSite)
      .then((next) => {
        if (!cancelled) {
          setView(next);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, selectedSite]);

  const save = useCallback(async () => {
    if (!view || !form || busy) return;

    // 只在"地址真的要变"时才问。客户改名字时每天都会点保存，
    // 每次都弹一句"旧地址会失效"等于训练他无脑点确定。
    const slugChanged = form.slug !== view.site.slug;
    if (slugChanged && view.site.published) {
      const ok = await confirm({
        title: '确认换地址？',
        message: (
          <>
            <p>
              站点已经发布过。换成 <strong>{form.slug}</strong> 之后，
              <strong>{view.site.primaryHost ?? `旧的地址`}</strong> 会立刻打不开 ——
              搜索引擎里的收录、名片和邮件签名里的链接都会失效。
            </p>
            <p>如果只是想让买家记住更好记的地址，绑定自己的域名更合适。</p>
          </>
        ),
        confirmText: '仍然更换',
        tone: 'danger',
      });
      if (!ok) return;
    }

    setBusy('save');
    setNotice(null);
    setError(null);
    try {
      const next = await updateOnboarding(
        tenantId,
        {
          siteName: form.siteName,
          /**
           * 地址只有主站有。第二个站不显示这个输入框，所以这里也不能把它
           * 作为 `undefined` 之外的任何值发出去 —— 服务端对非主站的 slug
           * 变更会直接 400（`slug_primary_site_only`）。
           */
          ...(view.site.isPrimary ? { slug: form.slug } : {}),
          defaultLocale: form.defaultLocale,
          locales: form.locales,
          // 服务端也会判一次：界面确认过不代表别人用脚本调用时也确认过
          ...(slugChanged && view.site.published ? { confirmSlugChange: true } : {}),
        },
        selectedSite,
      );
      setView(next);
      setForm(fromView(next));
      setExpanded(false);
      setNotice('已保存。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }, [busy, confirm, form, selectedSite, tenantId, view]);

  const publish = useCallback(async () => {
    if (busy) return;
    setBusy('publish');
    setNotice(null);
    setError(null);
    try {
      const result = await publishOnboarding(tenantId, selectedSite);
      setView(result.view);
      setNotice(
        result.published > 0
          ? `已发布 ${result.published} 个页面，现在可以从外面打开了。`
          : '所有页面都已经是发布状态，不需要再发一次。',
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_not_verified') {
        setError('先验证邮箱才能发布。验证邮件在注册时已经发出，顶部横幅可以重发。');
      } else {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    } finally {
      setBusy(null);
    }
  }, [busy, selectedSite, tenantId]);

  const pending = useMemo(
    () => (view ? view.checklist.filter((item) => !item.done) : []),
    [view],
  );

  if (error && !view) {
    // 读不出来时不显示整块卡片：一个"加载失败"的空卡片比没有这块更让人不安
    return null;
  }
  if (!view) return null;
  // 全部完成就自动收起。常驻不等于碍事 —— 它存在的意义是"还差什么"
  if (view.doneCount === view.totalCount && !expanded) return null;

  const live = view.site.published;

  return (
    <section className="onboard">
      <header className="onboard__head">
        <div>
          <h2 className="onboard__title">让你的站点上线</h2>
          <p className="onboard__sub">
            {pending.length > 0
              ? `还差 ${pending.length} 件事。也可以先去做别的，这张卡会一直在。`
              : '都做完了。'}
          </p>
        </div>
        <div className="onboard__aside">
          {/*
            多站才有切换器。单站客户看到一个只有一项的下拉框会以为漏了什么，
            而多站客户不加这个的话，卡片会一直显示"主站"的进度 ——
            他在第二个站上做完了全部步骤，这里还写着"还差 3 件事"。
          */}
          {view.sites.length > 1 ? (
            <label className="onboard__site">
              <span className="onboard__site-label">正在设置</span>
              <select
                className="onboard__site-select"
                value={selectedSite ?? view.site.siteId}
                onChange={(e) => {
                  // 换站时把编辑态与提示一起清掉：表单里是上一个站的值，
                  // 留着它再点保存就会把 A 站的名字写到 B 站
                  setSelectedSite(e.target.value);
                  setExpanded(false);
                  setForm(null);
                  setNotice(null);
                  setError(null);
                }}
              >
                {view.sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                    {site.isPrimary ? '（主站）' : ''}
                    {site.status === 'suspended' ? '（已暂停）' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <span className="onboard__progress">
            已完成 {view.doneCount} / {view.totalCount}
          </span>
        </div>
      </header>

      <div className="onboard__addr">
        <span className="onboard__addr-label">访问地址</span>
        {view.site.primaryHost ? (
          <code className="onboard__addr-host">
            {live ? `https://${view.site.primaryHost}` : view.site.primaryHost}
          </code>
        ) : (
          <span className="muted">还没有地址</span>
        )}
        {live && view.site.primaryHost ? (
          <Button
            variant="ghost"
            onClick={() => window.open(`https://${view.site.primaryHost}`, '_blank', 'noopener')}
          >
            打开
          </Button>
        ) : (
          <span className="muted">发布后可访问</span>
        )}
      </div>

      <ul className="onboard__list">
        {view.checklist.map((item) => (
          <li key={item.key} className={`onboard__item${item.done ? ' is-done' : ''}`}>
            <span className="onboard__mark" aria-hidden>
              {item.done ? '✓' : '○'}
            </span>
            <span className="onboard__label">{item.label}</span>
            {item.done ? null : <span className="onboard__hint">{item.hint}</span>}
            {item.done || item.key === 'email' ? null : (
              <button
                type="button"
                className="onboard__jump"
                onClick={() => {
                  // 「发布」在这张卡里就能做完；其余几项跳到真正能做事的地方。
                  // 「验证邮箱」不跳 —— 重发入口在顶部横幅，跳走反而绕远
                  if (item.key === 'contact') onNavigate('settings');
                  else if (item.key === 'product') onNavigate('catalog');
                  else if (item.key === 'domain') onNavigate('domains');
                  else if (item.key === 'published') void publish();
                }}
              >
                {item.key === 'published' ? '现在发布' : '去完成'}
              </button>
            )}
          </li>
        ))}
      </ul>

      {expanded && form ? (
        <div className="onboard__form">
          <label className="onboard__field">
            <span>站点名称</span>
            <input
              className="field"
              value={form.siteName}
              maxLength={80}
              onChange={(e) => setForm({ ...form, siteName: e.target.value })}
            />
            <span className="onboard__tip">
              会同步更新页面标题（浏览器标签上那一行）。你自己改过的标题不会被覆盖。
            </span>
          </label>

          {/*
            地址是**账号级**的（平台子域只有一行，挂在主站上）。第二个站也能
            改的话，要么撞唯一索引，要么给它塞一行域名而主站地址没变 ——
            两种都是客户看不懂的状态。所以这里直接不显示，并说明去哪改。
          */}
          {view.site.isPrimary ? (
            <label className="onboard__field">
              <span>访问地址</span>
              <div className="onboard__slug">
                <input
                  className="field"
                  value={form.slug}
                  spellCheck={false}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                />
                <span className="onboard__suffix">.{view.site.hostSuffix}</span>
              </div>
              <span className="onboard__tip">
                只在平台地址里生效；绑了自己的域名之后，买家看到的是那个域名。
              </span>
            </label>
          ) : (
            <div className="onboard__field">
              <span>访问地址</span>
              <span className="onboard__tip">
                {view.site.primaryHost ?? '还没有地址'}
                {' '}—— 平台地址是账号级的，要改请切到「主站」再改。这个站可以单独绑定自己的域名。
              </span>
            </div>
          )}

          <div className="onboard__field">
            <span>内容语言</span>
            <div className="chips">
              {LOCALES.map((locale) => {
                const isDefault = locale === form.defaultLocale;
                const checked = form.locales.includes(locale);
                // 配额满了以后，未勾选的语言禁用而不是"点了没反应"
                const blocked =
                  !checked &&
                  view.localeSettings.limit > 0 &&
                  form.locales.length >= view.localeSettings.limit;
                return (
                  <label key={locale} className="check">
                    <input
                      type="checkbox"
                      checked={checked}
                      // 默认语言和"配额已满时的新语言"都不能点：
                      // 让它能点但保存时被服务端拒（400），客户会以为自己填错了
                      disabled={isDefault || blocked}
                      onChange={() => {
                        const next = checked
                          ? form.locales.filter((l) => l !== locale)
                          : [...form.locales, locale];
                        setForm({
                          ...form,
                          locales: next.length > 0 ? next : form.locales,
                        });
                      }}
                    />
                    <span className={blocked ? 'muted' : undefined}>{LOCALE_LABEL[locale]}</span>
                  </label>
                );
              })}
            </div>
            <span className="onboard__tip">
              勾选的语言会出现在前台的切换器里；没有对应页面的语言访问时是 404。
              {view.localeSettings.limit > 0
                ? ` 当前套餐最多 ${view.localeSettings.limit} 种，已选 ${form.locales.length} 种。`
                : ''}
            </span>
          </div>

          <label className="onboard__field">
            <span>主语言</span>
            <select
              className="field"
              value={form.defaultLocale}
              onChange={(e) => {
                const locale = e.target.value as Locale;
                setForm({
                  ...form,
                  defaultLocale: locale,
                  locales: form.locales.includes(locale) ? form.locales : [...form.locales, locale],
                });
              }}
            >
              {form.locales.map((locale) => (
                <option key={locale} value={locale}>
                  {LOCALE_LABEL[locale]}
                </option>
              ))}
            </select>
            <span className="onboard__tip">买家直接打开你的地址时，看到的是这个语言。</span>
          </label>

          <div className="onboard__actions">
            <Button variant="primary" disabled={busy !== null} onClick={() => void save()}>
              {busy === 'save' ? '保存中…' : '保存'}
            </Button>
            <Button
              variant="ghost"
              disabled={busy !== null}
              onClick={() => {
                setExpanded(false);
                setForm(null);
                setError(null);
              }}
            >
              取消
            </Button>
          </div>
        </div>
      ) : (
        <div className="onboard__actions">
          <Button
            variant="primary"
            disabled={busy !== null || !emailVerified}
            onClick={() => void publish()}
          >
            {busy === 'publish' ? '发布中…' : view.publish.draftCount > 0 ? '发布草稿页' : '重新发布'}
          </Button>
          <Button
            variant="subtle"
            disabled={busy !== null}
            onClick={() => {
              setForm(fromView(view));
              setExpanded(true);
              setNotice(null);
            }}
          >
            修改站点信息
          </Button>
          {!emailVerified ? (
            <span className="muted">验证邮箱后才能发布</span>
          ) : view.publish.draftCount > 0 ? (
            <span className="muted">{view.publish.draftCount} 个草稿页</span>
          ) : null}
        </div>
      )}

      {notice ? <p className="notice notice--ok">{notice}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {confirmDialog}
    </section>
  );
}
